import { TeachingSite } from "@anu-its/cat-grpc-pb/js/anu/cat_app/site_pb";
import {
  Badge,
  Button,
  InlineAlert,
  Row,
  Table,
} from "@anu-its/ts-react-components";
import { useCallback, useEffect, useState } from "react";
import {
  dashboardSiteStatuses,
  siteStatusLabel,
  statusBadgeVariant,
} from "../constants/site-status";
import { SiteStatus } from "@anu-its/cat-grpc-pb/js/anu/cat/enums_pb";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { GetTeachingSites } from "../api/Sites";
import { LoadingOverlay } from "../components/Loading";
import { GearSharp } from "@anu-its/ts-react-components/icons/simple";
import { useRouter } from "next/navigation";
import { TableFilters } from "./TableFilters";
import { academicCareer } from "../constants/academic-career-codes";
import { deliveryModeNames } from "../constants/delivery-mode";
import { CanvasLink } from "../sites/[site_id]/helpers/CanvasLink";
import { TeachingSitesTableRow } from "@/app/dashboard/Interface";
import { useUserStore } from "@/app/providers/UserStore";
import { NewFilter } from "@/app/filters/interface";

export const TeachingSiteTable = ({
  page,
  pageSize,
  resetPage,
  handleTotalResults,
}: {
  page: number;
  pageSize: number;
  resetPage: () => void;
  handleTotalResults: (totalResults: number) => void;
}) => {
  const { userIsSuperAdmin } = useUserStore();
  const router = useRouter();

  const [nextCursor, setNextCursor] = useState("");
  const [siteData, setSiteData] = useState([] as Row[]);
  const [academicTermCode, setAcademicTermCode] = useState<string[]>([]);
  const [statusFilterList, setStatusFilterList] = useState<SiteStatus[]>(
    dashboardSiteStatuses,
  );
  const [courseQuery, setCourseQuery] = useState<string>("");

  const {
    data: sites,
    isLoading: sitesLoading,
    isFetching: sitesFetching,
    error: sitesError,
  } = useQuery({
    queryKey: [
      "GetTeachingSites",
      academicTermCode,
      page,
      statusFilterList,
      courseQuery,
    ],
    queryFn: () =>
      GetTeachingSites(
        pageSize,
        nextCursor,
        academicTermCode,
        statusFilterList,
        courseQuery,
        false,
      ),
    enabled: academicTermCode.length > 0,
    placeholderData: keepPreviousData,
  });

  const handleFiltersChange = useCallback(
    (newFilters: NewFilter, resetCursor: boolean) => {
      resetPage();
      if (resetCursor) {
        setNextCursor("");
      }

      setAcademicTermCode(newFilters.academicTermCode as string[]);
      setCourseQuery(newFilters.courseQuery as string);
      setStatusFilterList(newFilters.statusFilterList as SiteStatus[]);
    },
    [resetPage],
  );

  const columns = [
    {
      field: "courseCode",
      headerName: "Course Code",
      render: (value: string, row: TeachingSitesTableRow) =>
        (row.isLegacyLMS && userIsSuperAdmin) || !row.isLegacyLMS ? (
          <div
            onClick={() => router.push(`/sites/${row.id}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                router.push(`/sites/${row.id}`);
                e.preventDefault(); // Prevent scrolling when space is pressed
              }
            }}
            tabIndex={0}
            className="whitespace-pre-wrap cursor-pointer hover:underline"
            role="link"
            aria-label={`Navigate to ${value} details page`}
          >
            {value.includes("/") ? value.replaceAll("/", "\n") : value}
          </div>
        ) : (
          <div>{value.includes("/") ? value.replaceAll("/", "\n") : value}</div>
        ),
    },
    { field: "className", headerName: "Class Name" },
    { field: "classNumber", headerName: "Class Number" },
    { field: "academicOrg", headerName: "Academic Organisation" },
    { field: "deliveryMode", headerName: "Delivery Mode" },
    { field: "academicCareer", headerName: "Academic Career" },
    { field: "academicTerm", headerName: "Academic Term" },
    {
      field: "status",
      headerName: "Status",
      render: (value: SiteStatus, row: TeachingSitesTableRow) => {
        return (
          <div className="flex justify-center">
            <Badge
              size="md"
              variant={row.isLegacyLMS ? "notice" : statusBadgeVariant[value]}
            >
              {row.isLegacyLMS ? "Wattle" : siteStatusLabel(value)}
            </Badge>
          </div>
        );
      },
    },
    {
      field: "manage",
      headerName: "Manage",
      render: (_: string, row: TeachingSitesTableRow) => {
        return (
          <div className="flex gap-1">
            {(!row.isLegacyLMS || (row.isLegacyLMS && userIsSuperAdmin)) && (
              <Button
                variant="tertiary"
                classNames="!border-none"
                href={`/sites/${row.id}`}
                size="small"
                aria-label={`Navigate to ${row.courseCode || row.id} details page`}
              >
                <GearSharp size="lg" />
              </Button>
            )}
            {!row.isLegacyLMS && (
              <CanvasLink
                siteId={row.id}
                status={row.status}
                isIconOnly={true}
                aria-label={`View ${row.courseCode || row.id} in Canvas`}
              />
            )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (sites && !(sites instanceof Error)) {
      const sitesDataMapper = (sites: TeachingSite[]) => {
        const mappedSiteRoles: Row[] = [];
        sites?.forEach((siteObject) => {
          const site = siteObject.site.value;
          const siteType = siteObject.site.case;
          if (site && siteType) {
            const siteRow = {
              id: site.siteId,
              courseCode: site.courseCode,
              className: site.name,
              academicOrg: site.academicOrg?.orgDescription,
              academicCareer:
                "academicCareerCode" in site
                  ? academicCareer(site.academicCareerCode)
                  : null,
              academicTerm: site.academicTerm?.termDescription,
              deliveryMode:
                "deliveryModeCode" in site
                  ? deliveryModeNames(site.deliveryModeCode)
                  : null,
              classNumber:
                "classNumber" in site ? (
                  site.classNumber
                ) : siteType === "siteParent" ? (
                  <a href={`/sites/${site.siteId}?tab=Children+Sites`}>
                    Merged
                  </a>
                ) : siteType === "siteCommunity" ? (
                  <div>Community</div>
                ) : null,
              status: site.status,
              isLegacyLMS:
                site.$typeName !== "anu.cat_app.CommunitySite"
                  ? Boolean(site.legacyLms)
                  : false,
            };
            mappedSiteRoles.push(siteRow);
          }
        });
        return mappedSiteRoles;
      };

      setNextCursor(sites.cursor);
      const mappedData = sitesDataMapper(sites.sites);
      setSiteData([...mappedData]);
      handleTotalResults(Number(sites.totalResults));
    }
  }, [handleTotalResults, router, sites]);

  if (sitesLoading) return <LoadingOverlay />;

  if (sitesError || sites instanceof Error) {
    router.push("/error?errorType=data");
  }

  // TODO - placeholder filter for academic org - we can keep this commented until we have a legit endpoint (see Anu)

  return (
    <>
      {sitesFetching && <LoadingOverlay />}

      {/* Filters */}
      <TableFilters
        onFiltersChange={handleFiltersChange}
        showAcademicTerm={true}
        showStatus={true}
        showCourseCodeSearch={true}
      />
      {academicTermCode.length > 0 ? (
        <Table columns={columns} rows={siteData} />
      ) : (
        <InlineAlert variant="negative">
          No Academic Term Selected. Please select one in the filter.
        </InlineAlert>
      )}
    </>
  );
};
